/**
 * 需要想定 daily の未保存 (dirty) 派生の純関数。
 *
 * RetailerEditRow 単位・editRow 集合単位の dirty 判定を `computeForecastDiff` の
 * 唯一の呼び出し口として集約する。従来 useForecastDraftEditor 内に散っていた
 * `computeForecastDiff(row.baseline, fieldsSnapshotOf(row))` の再計算 (editRows
 * 初期化 / executeSaveAll の dirty 抽出 / discardAllDirty / 表示派生の 5+ 箇所) を
 * 本 module 経由に一本化する。dirty 判定の値意味論 (空セル null ≠ 0、取込元メタ
 * 差分、manualEditKomas 対称差) は `computeForecastDiff` が SoT で、本 module は
 * その入力整形 (baseline 比較用 snapshot) と row / 集合単位への畳み込みに徹する。
 */
import { computeForecastDiff } from './forecastDiff'
import type { RetailerFieldsSnapshot } from './types'

type DirtyRow = RetailerFieldsSnapshot & { baseline: RetailerFieldsSnapshot }

/**
 * RetailerEditRow から baseline 比較用の 6 field snapshot を取り出す。
 * manualEditKomas は Set のコピーを作る (呼び出し側の in-place 変更から独立させる)。
 */
export function fieldsSnapshotOf(
  row: RetailerFieldsSnapshot,
): RetailerFieldsSnapshot {
  return {
    forecastSlots: row.forecastSlots,
    providerSlots: row.providerSlots,
    manualEditKomas: new Set(row.manualEditKomas),
    provider: row.provider,
    providerOther: row.providerOther,
    providerLabel: row.providerLabel,
  }
}

/** row の現在値が baseline (= 直近の読込/保存値) と異なる (未保存の変更を持つ) か。 */
export function isRowDirty(row: DirtyRow): boolean {
  return computeForecastDiff(row.baseline, fieldsSnapshotOf(row)).isDirty
}

/** row の baseline と異なるコマ番号 (1-indexed) の集合。 */
export function rowDirtyKomas(row: DirtyRow): Set<number> {
  return computeForecastDiff(row.baseline, fieldsSnapshotOf(row)).dirtyKomas
}

/** rows のうち 1 つでも dirty な行があるか (保存ボタン活性の判定源、AC-039)。 */
export function anyRowDirty(rows: DirtyRow[]): boolean {
  return rows.some(isRowDirty)
}

/**
 * rows 全体の dirtyKomas の和集合 (BG全体〈複数小売〉ビュー用、AC-039 前段)。
 * 「いずれかの小売が dirty なコマ」の集合であり、集約値 (Σ小売 = BG) が実際に
 * 変わったコマの superset (2 小売が同コマを逆方向に等量変更して BG 合計が不変でも
 * 当該コマは含まれる)。変更コマの視覚区別 (AC-039) は真に変更されたセルを取り
 * こぼさない superset で満たせる (useForecastDraftEditor の bgTotalDirtyKomas
 * doc comment 参照)。
 */
export function unionDirtyKomas(rows: DirtyRow[]): Set<number> {
  const union = new Set<number>()
  for (const row of rows) {
    for (const koma of rowDirtyKomas(row)) union.add(koma)
  }
  return union
}
